import { useEffect, useRef, useState } from 'react';
import { matchRoutes, useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';
import { safeReturnTo } from '../../auth/safe-return-to';

/**
 * The sign-in's own two pages (App.tsx's `/login` and `/callback`), which
 * are never somewhere to return to. `/callback` is this route: navigating
 * to it keeps this screen mounted, `started` stops a second run, and a
 * signed-in person sits on "Completing sign-in…". `/login` sends a
 * signed-in person straight on to its own `returnTo`, so
 * `/login?returnTo=%2Fcallback` came back here by a full page load.
 */
const SIGNIN_PAGES = [{ path: '/login' }, { path: '/callback' }];

/** Where to land: the returned path, unless it is one of those pages. */
function landingFor(returned: string | void): string {
  const target = safeReturnTo(returned || null);
  // `matchRoutes`, not a string compare: the router mounts these pages for
  // `/CALLBACK/` and `/%63allback` as well (case, trailing slash, decoding).
  // Given a string it parses off the query itself, as navigation does.
  return matchRoutes(SIGNIN_PAGES, target) ? safeReturnTo(null) : target;
}

export function CallbackScreen() {
  const navigate = useNavigate();
  const { authMode, completeSignin } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    async function run() {
      try {
        // Where the person was going when they were sent to sign in -- a
        // locate link opened signed-out must end on that exact URL, not on
        // the Overview's default. `safeReturnTo` again here because this
        // is the line that navigates; with nothing to return to it gives
        // `/overview`, as this screen always did.
        const returnTo =
          authMode === 'zitadel' ? await completeSignin(window.location.href) : undefined;
        navigate(landingFor(returnTo), { replace: true });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Sign-in failed');
      }
    }

    void run();
  }, [authMode, completeSignin, navigate]);

  if (error) {
    return (
      <div role="alert" style={{ padding: 24 }}>
        <h1>Sign-in failed</h1>
        <p>{error}</p>
        <button type="button" onClick={() => navigate('/login', { replace: true })}>
          Back to login
        </button>
      </div>
    );
  }

  return <div style={{ padding: 24 }}>Completing sign-in…</div>;
}
